'use client'

import { ComponentType, SVGProps, useEffect, useState } from 'react'

import AnimotoLogo from '@/public/images_pwf/animoto.svg'
import DelivraLogo from '@/public/images_pwf/delivra.svg'
import DuplexLogo from '@/public/images_pwf/duplex.svg'
import LeadpagesLogo from '@/public/images_pwf/leadpages.svg'
import PavedLogo from '@/public/images_pwf/paved.svg'
import ShiftLogo from '@/public/images_pwf/shift.svg'

interface Brand {
  name: string
  url: string
  Logo: ComponentType<SVGProps<SVGSVGElement>>
  width: number
  height: number
  topOffset?: number
}

const brands: Record<string, Brand> = {
  animoto: {
    name: 'Animoto',
    url: 'https://animoto.com',
    Logo: AnimotoLogo,
    width: 115,
    height: 24,
  },
  delivra: {
    name: 'Delivra',
    url: 'https://www.delivra.com',
    Logo: DelivraLogo,
    width: 83,
    height: 18,
  },
  duplex: {
    name: 'Duplex',
    url: 'https://www.duplex.com',
    Logo: DuplexLogo,
    width: 72,
    height: 24,
    topOffset: 18.75,
  },
  leadpages: {
    name: 'Leadpages',
    url: 'https://www.leadpages.com',
    Logo: LeadpagesLogo,
    width: 129,
    height: 24,
  },
  paved: {
    name: 'Paved',
    url: 'https://www.paved.com',
    Logo: PavedLogo,
    width: 68,
    height: 24,
  },
  shift: {
    name: 'Shift',
    url: 'https://shift.com',
    Logo: ShiftLogo,
    width: 83,
    height: 24,
  },
}

interface FamilyOfBrandsProps {
  classNames?: {
    root?: string
    lead?: string
    container?: string
  }
  baseHeight?: {
    default: number
    lg: number
  }
}

export const FamilyOfBrands = ({
  classNames,
  baseHeight = { default: 24, lg: 32 },
}: FamilyOfBrandsProps) => {
  const [isLargeScreen, setIsLargeScreen] = useState(false)
  const brandsList = Object.values(brands)

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)')
    setIsLargeScreen(mediaQuery.matches)

    const handler = (e: MediaQueryListEvent) => setIsLargeScreen(e.matches)
    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])

  const currentBaseHeight = isLargeScreen ? baseHeight.lg : baseHeight.default

  return (
    <div className={classNames?.root}>
      <p className={classNames?.lead}>
        Paved is part of the{' '}
        <a
          className="underline"
          href="https://www.rdbrck.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Redbrick
        </a>{' '}
        family of brands.
      </p>
      <div className={classNames?.container}>
        {brandsList.map((brand) => {
          const { Logo } = brand
          const scale = brand.height > currentBaseHeight ? currentBaseHeight / brand.height : 1
          const scaledWidth = Math.round(brand.width * scale)
          const scaledHeight = Math.round(brand.height * scale)

          const topOffsetPx = brand.topOffset
            ? Math.round((brand.topOffset / 100) * currentBaseHeight)
            : 0
          const alignmentClass = topOffsetPx > 0 ? 'items-start' : 'items-center'

          return (
            <a
              key={brand.name}
              href={brand.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={brand.name}
              className="text-white-default"
            >
              <div
                className={`flex ${alignmentClass} justify-center`}
                style={{
                  height: currentBaseHeight,
                  paddingTop: topOffsetPx,
                  boxSizing: 'border-box',
                }}
              >
                <Logo width={scaledWidth} height={scaledHeight} />
              </div>
            </a>
          )
        })}
      </div>
    </div>
  )
}
